/**
 * MST store for the 3D IFC viewer. Owns:
 *   - the list of IFC_PROCESSED docs for the current building (`availableModels`)
 *   - the set of models currently loaded into the canvas (`loadedModels`); each
 *     entry carries its glTF Storage path (signed on demand by the viewer) and
 *     a `visible` flag for show/hide without unloading
 *   - the currently-selected element ({docId, guid}) — carries the owning doc
 *     so the property panel + selection highlight disambiguate across IFCs
 *
 * Multi-model wiring (WHA-3562). The `loadedModels` array's first entry is
 * the *coordinate anchor* — `useIfcViewer` uses its bbox centre to translate
 * all subsequent glTFs by the same offset so federated IFCs stay aligned.
 * Reordering this array is therefore semantically meaningful; we never sort
 * it.
 *
 * Async fetches live in `src/stores/ifcModel/repository.ts`. RootStore mounts
 * this as `ifcModelStore`.
 */

import { flow, types, type Instance, type SnapshotIn } from "mobx-state-tree";
import { track } from "@/lib/posthog";
import { changeCountBucket } from "@/lib/analytics/eventProps";
import {
  fetchIfcModelById,
  fetchIfcModelDiff,
  fetchIfcModelsForBuilding,
  type IfcChangeType,
  type IfcModelDiff,
  type IfcModelRow,
} from "./ifcModel/repository";

const LoadStatus = types.enumeration("LoadStatus", ["idle", "loading", "ready", "error"]);

/**
 * The in-viewer comparison the user just ran. Reported from here rather than from a
 * component effect because every way into compare mode — the `?compare=` deep link, the
 * re-target plaque, the swap button — goes through `startCompare`/`swapCompare`, and each
 * call is one applied selection: re-running the same pair later is a second comparison,
 * while re-rendering or re-styling the one on screen never reaches this code. Fires only
 * after the fetch resolves with a diff, so a failed or empty comparison counts nothing —
 * "no diff row" and "nothing changed" must not land in the same bucket.
 */
function reportCompared(diff: IfcModelDiff): void {
  const { added, removed, modified } = diff.summary;
  track("drawing_compared", {
    viewer: "ifc_3d",
    change_count_bucket: changeCountBucket(added + removed + modified),
  });
}

/** Snapshot of one row from public.documents (container_type='ifc'). */
const IfcModelSummary = types.model("IfcModelSummary", {
  id: types.number,
  filename: types.string,
  state: types.string,
  file_path: types.string,
  size: types.number,
  created_at: types.string,
  /** Owning project — issues created from a 3D element default to it (WHA-3697). */
  project_id: types.maybeNull(types.number),
  /** Folder this model is filed under — drives the picker's folder-path hover
   * tooltip (WHA-3708). */
  folder_id: types.maybeNull(types.number),
  /** Head of its version chain? `latestModels` lists only heads at the top
   * level; older versions are nested under them in the picker (WHA-3712). A
   * model deep-linked to a superseded file is flagged false (WHA-3688). */
  is_latest_version: types.maybeNull(types.boolean),
  /** Version number within its chain (1-based) — shown as a `v{n}` badge. */
  version: types.maybeNull(types.number),
  /** Shared id linking every version of the same file (WHA-3712). */
  version_group: types.maybeNull(types.string),
  ifc_schema: types.maybeNull(types.string),
  element_count: types.maybeNull(types.number),
  storey_count: types.maybeNull(types.number),
  gltf_path: types.maybeNull(types.string),
});

/** One entry per IFC currently loaded into the scene.
 *
 * - `gltfPath` is the Storage file path (e.g. `<building>/<uuid>.glb`) and is
 *   populated synchronously when the model is added. The viewer's
 *   `fetchWithUrlRefresh` (WHA-3578) re-signs from this path if a stale URL
 *   throws 401/403 — so long viewer sessions survive past the 1 h TTL.
 * - `visible` toggles three.js Object3D.visible without unloading geometry —
 *   the user can flip piping off and back on instantly.
 */
const LoadedModel = types.model("LoadedModel", {
  docId: types.number,
  gltfPath: types.maybeNull(types.string),
  visible: types.optional(types.boolean, true),
});

/** Identifies a selected IFC element. Carries the owning doc id because two
 * federated IFCs can theoretically share a GlobalId, and the property RPC
 * needs both anyway. */
const SelectedElement = types.model("SelectedElement", {
  docId: types.number,
  guid: types.string,
});

/** Identifies a class group picked in the spatial tree ("Wall · 42" under a
 * storey). Unlike containers, a class group has no element GUID of its own —
 * its members are resolved via the get_ifc_node_elements RPC.
 *
 * `storeyGuid` is null for the document-wide variant (WHA: "highlight all
 * similar elements"), where members come from get_ifc_elements_by_class
 * instead of the storey-scoped get_ifc_node_elements. */
const ClassGroupSelection = types.model("ClassGroupSelection", {
  docId: types.number,
  storeyGuid: types.maybeNull(types.string),
  ifcClass: types.string,
});

/** A spatial container picked in the tree. Carries the node's ifc_class so the
 * canvas can branch: Project/Site/Building span the whole model and highlight
 * client-side (no member-GUID RPC, WHA-3689); storeys/spaces resolve members
 * via get_ifc_node_member_guids. */
const ContainerSelection = types.model("ContainerSelection", {
  docId: types.number,
  guid: types.string,
  ifcClass: types.string,
});

/** WHA-3695: which change categories are currently shown in the diff view. The
 * canvas styling effect reads these to drop a category from the tinted/ghosted
 * sets; the diff panel reads them to filter its list. */
const ChangeVisibility = types.model("ChangeVisibility", {
  added: types.optional(types.boolean, true),
  removed: types.optional(types.boolean, true),
  modified: types.optional(types.boolean, true),
  unchanged: types.optional(types.boolean, true),
});

/** WHA-3695 cont.4: which side of a comparison is rendered. */
export type CompareModelSide = "newer" | "older";

/** WHA-3695 cont.5: which changed elements actually carry geometry in 3D.
 * Resolved by the canvas from the viewer's per-model guidMap once both models
 * load. Many IFC changes are non-geometric (IfcOpeningElement voids, MEP ports,
 * bodyless walls) so they can't appear in 3D; the panel uses this to mark them
 * "not in 3D" instead of looking like isolate dropped them. */
interface ChangeDrawability {
  /** Changed-element GUIDs with no mesh in either compared model. */
  nonDrawableGuids: string[];
  counts: {
    added: { drawable: number; total: number };
    removed: { drawable: number; total: number };
    modified: { drawable: number; total: number };
  };
}

export type LoadedModelInstance = Instance<typeof LoadedModel>;
export type IfcModelSummaryInstance = Instance<typeof IfcModelSummary>;

/** Human label for a model summary: `filename vN` (or just the filename when the
 * version is unknown). Empty string for a missing summary so callers can render
 * it directly. */
export const formatModelLabel = (
  s?: { filename: string; version: number | null } | null
): string => (s ? (s.version != null ? `${s.filename} v${s.version}` : s.filename) : "");
export type SelectedElementSnapshot = SnapshotIn<typeof SelectedElement>;
export type ClassGroupSelectionSnapshot = SnapshotIn<typeof ClassGroupSelection>;
export type ContainerSelectionSnapshot = SnapshotIn<typeof ContainerSelection>;

function rowToSummary(row: IfcModelRow): Instance<typeof IfcModelSummary> {
  const meta = row.cloud_metadata ?? {};
  const pick = (key: string): string | null =>
    typeof meta[key] === "string" ? (meta[key] as string) : null;
  return IfcModelSummary.create({
    id: row.id,
    filename: row.filename,
    state: row.state,
    file_path: row.file_path,
    size: row.size,
    created_at: row.created_at,
    project_id: row.project_id,
    folder_id: row.folder_id ?? null,
    is_latest_version: row.is_latest_version ?? null,
    version: row.version ?? null,
    version_group: row.version_group ?? null,
    ifc_schema: pick("ifc_schema"),
    element_count: typeof meta.element_count === "number" ? meta.element_count : null,
    storey_count: typeof meta.storey_count === "number" ? meta.storey_count : null,
    gltf_path: pick("gltf_path"),
  });
}

export const IfcModelStore = types
  .model("IfcModelStore", {
    /** All IFC_PROCESSED documents for the active building (newest first). */
    availableModels: types.array(IfcModelSummary),
    /** Models currently loaded into the canvas. Order is significant — index 0
     * is the coordinate anchor (see useIfcViewer's sceneOffsetRef). */
    loadedModels: types.array(LoadedModel),
    /** Currently-selected element across any loaded model, or null. */
    selectedElement: types.maybeNull(SelectedElement),
    /** WHA-3635: a spatial container (storey/building/…) picked in the tree. When
     * set, the canvas highlights all the elements it contains (whole-model
     * containers client-side; storeys/spaces via the get_ifc_node_member_guids
     * RPC — WHA-3689) instead of the single-element highlight.
     * Cleared by any direct element selection. */
    highlightContainer: types.maybeNull(ContainerSelection),
    /** WHA-3687: a class group ("Wall · 42") picked in the tree. When set, the
     * canvas highlights all members of that class in the storey (resolved via
     * the get_ifc_node_elements RPC). Mutually exclusive with selectedElement
     * and highlightContainer — a class group has no element GUID, so the
     * Properties rail stays empty while one is active. */
    highlightClassGroup: types.maybeNull(ClassGroupSelection),

    loadStatus: types.optional(LoadStatus, "idle"),
    error: types.maybeNull(types.string),

    /** Building / org context for the currently loaded list. */
    buildingId: types.maybeNull(types.number),
    orgId: types.maybeNull(types.string),

    /** Global building scope (WHA): when set, the picker list + empty state are
     * narrowed to this project's IFC files. Null = whole building (all models).
     * Loading a specific model by id (e.g. an `?ifc=` deep-link) is unaffected,
     * so a model from another project can still be opened directly. */
    scopeProjectId: types.maybeNull(types.number),

    // ---- Compare / diff mode (WHA-3695) ----------------------------------
    /** Base (older) document id of an active comparison, or null when not
     * comparing. Both compare ids non-null ⇒ the viewer is in diff mode. */
    compareBaseDocId: types.maybeNull(types.number),
    /** Target (newer) document id of an active comparison. */
    compareTargetDocId: types.maybeNull(types.number),
    /** The diff payload from get_ifc_model_diff (frozen plain JSON), or null
     * until it resolves. */
    diff: types.frozen<IfcModelDiff | null>(null),
    compareStatus: types.optional(LoadStatus, "idle"),
    compareError: types.maybeNull(types.string),
    /** Which change categories are shown (3D tint + panel list). */
    compareVisibility: types.optional(ChangeVisibility, {}),
    /** GUID of the changed element selected for the detail diff, or null. */
    selectedChangeGuid: types.maybeNull(types.string),
    /** GUIDs of a change-group selected in the browser, highlighted+framed in
     * 3D (WHA-3695 follow-up). Empty when no group is active. */
    selectedGroupGuids: types.array(types.string),
    /** WHA-3695 cont.4: which single model renders in compare mode. "newer" =
     * the target (highlights added + modified); "older" = the base (highlights
     * removed + modified). Only the active model is rendered; the other is
     * hidden (geometry stays loaded). */
    activeCompareModel: types.optional(
      types.enumeration<CompareModelSide>(["newer", "older"]),
      "newer"
    ),
    /** WHA-3695 cont.4: faceted render-filter selections for compare mode. Each
     * empty array = that facet is unconstrained. The rendered set is the
     * intersection across non-empty facets (values within a facet are OR'd);
     * non-matching elements are hidden. GUID resolution happens in the canvas
     * effect (these hold only the selected facet values). */
    filterStoreyGuids: types.array(types.string),
    filterDisciplines: types.array(types.string),
    filterClasses: types.array(types.string),
    /** WHA: render IfcSpace meshes? Default false — spaces carry real geometry
     * (in the glb since the pipeline stopped excluding them) but would occlude
     * the model, so the canvas hides them by default and shows them translucent
     * when toggled on. */
    spacesVisible: types.optional(types.boolean, false),
    /** WHA-3695 cont.5: geometry-presence of the changed elements, resolved from
     * the loaded models' guidMaps by the canvas. Null until both models load. */
    changeDrawability: types.frozen<ChangeDrawability | null>(null),
  })
  .views((self) => ({
    get renderableByGuid(): Map<string, boolean> {
      const m = new Map<string, boolean>();
      for (const c of self.diff?.changes ?? []) {
        if (typeof c.renderable === "boolean") m.set(c.guid, c.renderable);
      }
      return m;
    },
  }))
  .views((self) => ({
    /** Summaries (filename, schema, etc.) joined against availableModels, in
     * loadedModels order — components render dropdown rows / section headers
     * straight from this. */
    get loadedModelSummaries(): IfcModelSummaryInstance[] {
      const out: IfcModelSummaryInstance[] = [];
      for (const lm of self.loadedModels) {
        const summary = self.availableModels.find((m) => m.id === lm.docId);
        if (summary) out.push(summary);
      }
      return out;
    },
    get loadedModelDocIds(): number[] {
      return self.loadedModels.map((m) => m.docId);
    },
    /** Loaded AND switched on with the eye icon, in load order. A saved
     * viewpoint records these so a restore reproduces what the reporter
     * actually saw, rather than everything that happened to be loaded. */
    get visibleModelDocIds(): number[] {
      return self.loadedModels.filter((m) => m.visible).map((m) => m.docId);
    },
    /** How many models the active project scope holds — what `hasModels`
     * answers yes/no on. Exposed so anything reporting on readiness counts the
     * same set the readiness was decided from. */
    get scopedModelCount(): number {
      return self.scopeProjectId == null
        ? self.availableModels.length
        : self.availableModels.filter((m) => m.project_id === self.scopeProjectId).length;
    },
    get hasModels() {
      return this.scopedModelCount > 0;
    },
    /** Top-level picker list: one row per file (the head of each version
     * chain). `is_latest_version` null is legacy single-version data, treated
     * as a head. Preserves `availableModels`' newest-first order (WHA-3712).
     * Narrowed to the active project scope when one is set. */
    get latestModels(): IfcModelSummaryInstance[] {
      return self.availableModels.filter(
        (m) =>
          m.is_latest_version !== false &&
          (self.scopeProjectId == null || m.project_id === self.scopeProjectId)
      );
    },
    /** Superseded versions of a chain, newest version first. Empty for a file
     * that was never re-versioned (WHA-3712). */
    olderVersions(versionGroup: string | null): IfcModelSummaryInstance[] {
      if (!versionGroup) return [];
      return self.availableModels
        .filter((m) => m.version_group === versionGroup && m.is_latest_version === false)
        .slice()
        .sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
    },
    /** Every version of the file `docId` belongs to, newest version first.
     * Just `[docId's summary]` when it has no version group. Used by the
     * preview overlay's versions-only picker (WHA-3712). */
    versionChain(docId: number): IfcModelSummaryInstance[] {
      const doc = self.availableModels.find((m) => m.id === docId);
      if (!doc) return [];
      if (!doc.version_group) return [doc];
      return self.availableModels
        .filter((m) => m.version_group === doc.version_group)
        .slice()
        .sort((a, b) => (b.version ?? 0) - (a.version ?? 0));
    },
    isLoaded(docId: number): boolean {
      return self.loadedModels.some((m) => m.docId === docId);
    },
    getLoaded(docId: number): LoadedModelInstance | null {
      return self.loadedModels.find((m) => m.docId === docId) ?? null;
    },

    // ---- Compare / diff views (WHA-3695) ---------------------------------
    /** True once both compare ids are set — the viewer renders diff chrome. */
    get isComparing(): boolean {
      return self.compareBaseDocId != null && self.compareTargetDocId != null;
    },
    /** GUIDs of elements present only in the target (B). */
    get addedGuids(): string[] {
      return (self.diff?.changes ?? []).filter((c) => c.change_type === "added").map((c) => c.guid);
    },
    /** GUIDs of elements present only in the base (A). */
    get removedGuids(): string[] {
      return (self.diff?.changes ?? [])
        .filter((c) => c.change_type === "removed")
        .map((c) => c.guid);
    },
    /** GUIDs of elements present in both but changed. */
    get modifiedGuids(): string[] {
      return (self.diff?.changes ?? [])
        .filter((c) => c.change_type === "modified")
        .map((c) => c.guid);
    },
    /** Whether a change category is currently shown. */
    isChangeTypeVisible(type: IfcChangeType | "unchanged"): boolean {
      return self.compareVisibility[type];
    },
    /** WHA-3695 cont.4: the doc id of the model currently rendered in compare
     * mode (newer→target, older→base), or null when not comparing. */
    get activeCompareDocId(): number | null {
      return self.activeCompareModel === "newer" ? self.compareTargetDocId : self.compareBaseDocId;
    },
    /** The doc id of the model hidden in compare mode (the non-active side). */
    get inactiveCompareDocId(): number | null {
      return self.activeCompareModel === "newer" ? self.compareBaseDocId : self.compareTargetDocId;
    },
    /** WHA-3695 cont.4: which change types tint the active model, with their
     * GUIDs — newer shows added + modified, older shows removed + modified,
     * each gated by the change-type visibility chips. The canvas maps each
     * `type` to its DIFF_COLOR. */
    get activeTintSpec(): { type: IfcChangeType; guids: string[] }[] {
      const changes = self.diff?.changes ?? [];
      const guidsOf = (t: IfcChangeType) =>
        changes.filter((c) => c.change_type === t).map((c) => c.guid);
      const types: IfcChangeType[] =
        self.activeCompareModel === "newer" ? ["added", "modified"] : ["removed", "modified"];
      return types
        .filter((t) => self.compareVisibility[t])
        .map((t) => ({ type: t, guids: guidsOf(t) }));
    },
    /** True when any render facet (floor/discipline/class) is constraining. */
    get hasCompareFilter(): boolean {
      return (
        self.filterStoreyGuids.length > 0 ||
        self.filterDisciplines.length > 0 ||
        self.filterClasses.length > 0
      );
    },
    /** WHA-3695 cont.5 / WHA: does this changed element have geometry to show in
     * 3D? Prefers the authoritative server flag (data.ifc_elements.has_geometry);
     * falls back to the glTF-inferred drawability for pre-renderable payloads,
     * defaulting true until that resolves so nothing is greyed mid-load. */
    isChangeDrawable(guid: string): boolean {
      const auth = self.renderableByGuid;
      if (auth.size > 0) return auth.get(guid) ?? false;
      const d = self.changeDrawability;
      return d ? !d.nonDrawableGuids.includes(guid) : true;
    },
    /** WHA-3749: GUIDs that carry a diff entry, for O(1) click-to-select when an
     * element is picked in the 3D scene. Cached until `diff` changes. */
    get changedGuids(): Set<string> {
      return new Set((self.diff?.changes ?? []).map((c) => c.guid));
    },
  }))
  .actions((self) => {
    let compareRequestId = 0;
    let pendingComparePair: { baseDocId: number; targetDocId: number } | null = null;

    const isCurrentCompareRequest = (requestId: number) => requestId === compareRequestId;
    const cancelPendingCompare = () => {
      compareRequestId += 1;
      pendingComparePair = null;
    };

    const addModel = (docId: number) => {
      const summary = self.availableModels.find((m) => m.id === docId);
      if (!summary) {
        self.error = `Model ${docId} not in availableModels for this building`;
        return;
      }
      if (self.loadedModels.some((m) => m.docId === docId)) return;
      // WHA-3729 follow-up: at most one version of a given file may be loaded at
      // once. Selecting a version auto-replaces any other loaded version of the
      // SAME file — matched by `version_group` (null = single-version file, which
      // is never cross-removed). Covers the picker, the overlay version list, and
      // ?ifc= hydration (all funnel through addModel).
      if (summary.version_group) {
        for (const loaded of self.loadedModels.slice()) {
          const loadedSummary = self.availableModels.find((m) => m.id === loaded.docId);
          if (loadedSummary?.version_group === summary.version_group) {
            removeModel(loaded.docId);
          }
        }
      }
      // gltfPath is the canonical file path the viewer signs on demand via its
      // fetchWithUrlRefresh wrapper (WHA-3578) — no eager signing here.
      self.loadedModels.push(
        LoadedModel.create({
          docId,
          gltfPath: summary.gltf_path,
          visible: true,
        })
      );
    };

    const removeModel = (docId: number) => {
      const idx = self.loadedModels.findIndex((m) => m.docId === docId);
      if (idx === -1) return;
      self.loadedModels.splice(idx, 1);
      if (self.selectedElement?.docId === docId) {
        self.selectedElement = null;
      }
      if (self.highlightContainer?.docId === docId) {
        self.highlightContainer = null;
      }
      if (self.highlightClassGroup?.docId === docId) {
        self.highlightClassGroup = null;
      }
    };

    /** Make sure `docId` has a summary in `availableModels` so it can be loaded.
     * No-op when it's already there (the normal picker case). When it isn't —
     * the only real case is a PM deep-link to a superseded revision that the
     * latest-only list filtered out (WHA-3688) — fetch that exact doc by id
     * (bypassing the is_latest_version filter) and append it. Returns true once
     * the doc is available, false if it couldn't be resolved. */
    const ensureModelAvailable = flow(function* ensureModelAvailable(docId: number) {
      if (self.availableModels.some((m) => m.id === docId)) return true;
      if (!self.orgId) return false;
      try {
        const row = (yield fetchIfcModelById(docId, self.orgId)) as IfcModelRow | null;
        if (!row) return false;
        self.availableModels.push(rowToSummary(row));
        return true;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        return false;
      }
    });

    /** Atomic diff: remove docs not in the target set, add docs not currently
     * loaded. Picker → store handler is a one-liner that calls this. */
    const setLoadedModels = flow(function* setLoadedModels(docIds: number[]) {
      const target = new Set(docIds);
      // Snapshot existing ids before mutating so we know what to add.
      const currentlyLoaded = new Set(self.loadedModels.map((m) => m.docId));
      // Drop docs not in target.
      for (const m of self.loadedModels.slice()) {
        if (!target.has(m.docId)) removeModel(m.docId);
      }
      // Add docs not yet loaded, preserving the caller's order. Ensure each is
      // available first so a deep-linked older revision (absent from the
      // latest-only list) still loads; truly unresolvable ids are skipped.
      for (const id of docIds) {
        if (!currentlyLoaded.has(id)) {
          const ok = (yield ensureModelAvailable(id)) as boolean;
          if (ok) addModel(id);
        }
      }
    });

    const setModelVisibility = (docId: number, visible: boolean) => {
      const entry = self.loadedModels.find((m) => m.docId === docId);
      if (entry) entry.visible = visible;
    };

    // WHA-3695 cont.4 / WHA-3750: add/remove a value from one of the render-filter
    // facet arrays (storey / discipline / class), shared by both the diff view
    // and the normal-view Filters menu. MST string arrays satisfy this shape.
    const toggleArrayMember = (
      arr: {
        indexOf(v: string): number;
        push(v: string): void;
        splice(i: number, n: number): void;
      },
      key: string
    ) => {
      const idx = arr.indexOf(key);
      if (idx === -1) arr.push(key);
      else arr.splice(idx, 1);
    };

    const selectElement = (payload: SelectedElementSnapshot | null) => {
      self.selectedElement = payload ? SelectedElement.create(payload) : null;
      // A direct element pick supersedes any container/class highlight (WHA-3635).
      self.highlightContainer = null;
      self.highlightClassGroup = null;
    };

    /** WHA-3635: pick a spatial container from the tree. Sets the selection (so
     * the Properties panel shows the container's metadata) AND flags the
     * container so the canvas highlights all its contained elements + frames it.
     * Carries the node's ifc_class so whole-model containers can highlight
     * without the member-GUID RPC (WHA-3689). */
    const selectContainer = (payload: ContainerSelectionSnapshot) => {
      self.selectedElement = SelectedElement.create({
        docId: payload.docId,
        guid: payload.guid,
      });
      self.highlightContainer = ContainerSelection.create(payload);
      self.highlightClassGroup = null;
    };

    /** WHA-3687: pick a class group ("Wall · 42") from the tree. Highlights +
     * frames every member of the class in that storey. Clears the element
     * selection — a class has no element GUID, so the Properties rail has
     * nothing truthful to show. */
    const selectClassGroup = (payload: ClassGroupSelectionSnapshot) => {
      self.selectedElement = null;
      self.highlightContainer = null;
      self.highlightClassGroup = ClassGroupSelection.create(payload);
    };

    /** WHA: "highlight all similar elements" — from a picked element, light up
     * every element of its IFC class across the whole model (storeyGuid null →
     * resolved via get_ifc_elements_by_class). Keeps `selectedElement` so the
     * Properties panel (and its button) stay visible; the selection-highlight
     * effect yields to the class-group highlight while one is active. Calling it
     * again for the same {docId, ifcClass} toggles it off, restoring the
     * single-element highlight. */
    const highlightSimilarClass = (payload: { docId: number; ifcClass: string }) => {
      const cur = self.highlightClassGroup;
      if (
        cur &&
        cur.storeyGuid == null &&
        cur.docId === payload.docId &&
        cur.ifcClass === payload.ifcClass
      ) {
        self.highlightClassGroup = null;
        return;
      }
      self.highlightContainer = null;
      self.highlightClassGroup = ClassGroupSelection.create({
        docId: payload.docId,
        storeyGuid: null,
        ifcClass: payload.ifcClass,
      });
    };

    // ---- Compare / diff mode (WHA-3695) ----------------------------------
    /** Enter diff mode comparing `baseDocId` (older) against `targetDocId`
     * (newer). Loads BOTH glTFs at once — bypassing addModel's version-group
     * auto-replace, which would otherwise unload one version when the other is
     * added — then fetches the structural diff. The target is pushed first so
     * it is the coordinate anchor (two versions of one file share coordinates,
     * so alignment is exact).
     *
     * Resolves TRUE only once the pair has been adopted (both glTFs loaded and
     * `compareBaseDocId`/`compareTargetDocId` written), FALSE when the request was
     * dropped or could not load. A caller that owns URL state must write `?compare=`
     * on true alone — the URL follows the store, it never leads it. */
    const startCompare = flow(function* startCompare(baseDocId: number, targetDocId: number) {
      // The compare deep-link effect can repeat the same request while the first call is
      // still resolving model summaries. Collapse that duplicate, but let a different pair
      // supersede the in-flight request so a picker re-target is never dropped.
      if (
        pendingComparePair?.baseDocId === baseDocId &&
        pendingComparePair.targetDocId === targetDocId
      ) {
        return false;
      }
      const requestId = ++compareRequestId;
      pendingComparePair = { baseDocId, targetDocId };
      self.compareStatus = "loading";
      self.compareError = null;
      const okBase = (yield ensureModelAvailable(baseDocId)) as boolean;
      if (!isCurrentCompareRequest(requestId)) return false;
      const okTarget = (yield ensureModelAvailable(targetDocId)) as boolean;
      if (!isCurrentCompareRequest(requestId)) return false;
      if (!okBase || !okTarget) {
        self.compareStatus = "error";
        self.compareError = "Could not load one or both IFC models to compare.";
        pendingComparePair = null;
        return false;
      }
      const baseSummary = self.availableModels.find((m) => m.id === baseDocId);
      const targetSummary = self.availableModels.find((m) => m.id === targetDocId);

      // Load both versions directly (skip the version_group dedup in addModel).
      // Both stay loaded (visible) so switching is instant; the canvas
      // diff-styling effect renders ONLY the active model and hides the other
      // via mesh styling (WHA-3695 cont.4) — NOT loadedModels.visible, because
      // model registration can lag the "ready" status, so the visibility effect
      // would fire too early (model not in the viewer yet) and never re-hide.
      self.loadedModels.clear();
      self.loadedModels.push(
        LoadedModel.create({
          docId: targetDocId,
          gltfPath: targetSummary?.gltf_path ?? null,
          visible: true,
        })
      );
      self.loadedModels.push(
        LoadedModel.create({
          docId: baseDocId,
          gltfPath: baseSummary?.gltf_path ?? null,
          visible: true,
        })
      );
      self.selectedElement = null;
      self.highlightContainer = null;
      self.highlightClassGroup = null;
      self.selectedChangeGuid = null;
      self.compareBaseDocId = baseDocId;
      self.compareTargetDocId = targetDocId;
      // Start on the newer model with no render filter (WHA-3695 cont.4).
      self.activeCompareModel = "newer";
      self.filterStoreyGuids.clear();
      self.filterDisciplines.clear();
      self.filterClasses.clear();
      self.changeDrawability = null;

      try {
        const d = (yield fetchIfcModelDiff(baseDocId, targetDocId)) as IfcModelDiff | null;
        if (!isCurrentCompareRequest(requestId)) return false;
        self.diff = d;
        self.compareStatus = d ? "ready" : "error";
        if (!d) self.compareError = "Comparison returned no data (model not visible).";
        if (d) reportCompared(d);
      } catch (e) {
        if (!isCurrentCompareRequest(requestId)) return false;
        self.compareStatus = "error";
        self.compareError = e instanceof Error ? e.message : String(e);
      }
      pendingComparePair = null;
      // Adopted either way: a failed diff still leaves THIS pair on screen (with its
      // error), so a URL naming it stays truthful.
      return true;
    });

    /** Flip which model is base (older) and which is target (newer), then
     * recompute the diff with the sides swapped — added↔removed invert and the
     * modified deltas reverse direction. Both models are already loaded, so this
     * only re-fetches the diff (no glTF reload). Resets the selection, visibility
     * chips, render filters and active side, like entering a fresh comparison. */
    const swapCompare = flow(function* swapCompare() {
      if (self.compareBaseDocId == null || self.compareTargetDocId == null) return;
      const newBase = self.compareTargetDocId;
      const newTarget = self.compareBaseDocId;
      const requestId = ++compareRequestId;
      pendingComparePair = { baseDocId: newBase, targetDocId: newTarget };
      self.compareBaseDocId = newBase;
      self.compareTargetDocId = newTarget;
      self.compareStatus = "loading";
      self.compareError = null;
      self.diff = null;
      self.selectedChangeGuid = null;
      self.selectedGroupGuids.clear();
      self.selectedElement = null;
      self.activeCompareModel = "newer";
      self.compareVisibility = ChangeVisibility.create({});
      self.filterStoreyGuids.clear();
      self.filterDisciplines.clear();
      self.filterClasses.clear();
      self.changeDrawability = null;
      try {
        const d = (yield fetchIfcModelDiff(newBase, newTarget)) as IfcModelDiff | null;
        if (!isCurrentCompareRequest(requestId)) return;
        self.diff = d;
        self.compareStatus = d ? "ready" : "error";
        if (!d) self.compareError = "Comparison returned no data (model not visible).";
        if (d) reportCompared(d);
      } catch (e) {
        if (!isCurrentCompareRequest(requestId)) return;
        self.compareStatus = "error";
        self.compareError = e instanceof Error ? e.message : String(e);
      }
      pendingComparePair = null;
    });

    /** Leave diff mode: clear the comparison and unload both models so the
     * normal single-model picker flow resumes clean. */
    const exitCompare = () => {
      cancelPendingCompare();
      self.compareBaseDocId = null;
      self.compareTargetDocId = null;
      self.diff = null;
      self.compareStatus = "idle";
      self.compareError = null;
      self.compareVisibility = ChangeVisibility.create({});
      self.selectedChangeGuid = null;
      self.selectedGroupGuids.clear();
      self.selectedElement = null;
      self.activeCompareModel = "newer";
      self.filterStoreyGuids.clear();
      self.filterDisciplines.clear();
      self.filterClasses.clear();
      self.changeDrawability = null;
      self.loadedModels.clear();
    };

    const toggleChangeVisibility = (type: IfcChangeType | "unchanged") => {
      self.compareVisibility[type] = !self.compareVisibility[type];
    };

    /** WHA-3695 cont.5: the canvas resolves which changed elements have geometry
     * (from the loaded models' guidMaps) and pushes the result here for the
     * panel to flag non-geometric changes. */
    const setChangeDrawability = (drawability: ChangeDrawability | null) => {
      self.changeDrawability = drawability;
    };

    /** Select a changed element for the detail diff. Also drives the canvas
     * selection highlight + camera framing: a removed element lives only in the
     * base model, everything else in the target. */
    const selectChange = (guid: string | null) => {
      self.selectedChangeGuid = guid;
      self.selectedGroupGuids.clear();
      if (!guid) {
        self.selectedElement = null;
        return;
      }
      const change = self.diff?.changes.find((c) => c.guid === guid);
      const docId =
        change?.change_type === "removed" ? self.compareBaseDocId : self.compareTargetDocId;
      self.selectedElement = docId != null ? SelectedElement.create({ docId, guid }) : null;
      self.highlightContainer = null;
      self.highlightClassGroup = null;
    };

    /** Highlight + frame every element of a change-group (picked in the diff
     * browser) in 3D. Clears any single-element selection. */
    const selectGroup = (guids: string[]) => {
      self.selectedGroupGuids.replace(guids);
      self.selectedChangeGuid = null;
      self.selectedElement = null;
      self.highlightContainer = null;
      self.highlightClassGroup = null;
    };

    // WHA-3695 cont.4 — active model + render filters --------------------------
    /** Switch which single model renders in compare mode. The canvas
     * diff-styling effect reacts to `activeCompareModel` and re-styles so only
     * the chosen model shows. Drops any open element selection (it may belong to
     * the now-hidden model). */
    const setActiveCompareModel = (side: CompareModelSide) => {
      self.activeCompareModel = side;
      self.selectedChangeGuid = null;
      self.selectedElement = null;
    };
    // Reuses toggleArrayMember (defined above for discipline filters) — the
    // facet arrays are the same types.array(types.string) shape.
    const toggleFilterStorey = (storeyGuid: string) => {
      toggleArrayMember(self.filterStoreyGuids, storeyGuid);
    };
    const toggleFilterDiscipline = (disciplineKey: string) => {
      toggleArrayMember(self.filterDisciplines, disciplineKey);
    };
    const toggleFilterClass = (ifcClass: string) => {
      toggleArrayMember(self.filterClasses, ifcClass);
    };
    const clearCompareFilters = () => {
      self.filterStoreyGuids.clear();
      self.filterDisciplines.clear();
      self.filterClasses.clear();
    };
    const toggleSpacesVisible = () => {
      self.spacesVisible = !self.spacesVisible;
    };

    const loadModelsForBuilding = flow(function* loadModelsForBuilding(
      buildingId: number,
      orgId: string
    ) {
      self.loadStatus = "loading";
      self.error = null;
      self.buildingId = buildingId;
      self.orgId = orgId;
      try {
        const rows: IfcModelRow[] = yield fetchIfcModelsForBuilding(buildingId, orgId);
        self.availableModels.replace(rows.map(rowToSummary));

        // Reconcile loaded models against the freshly-fetched availability:
        // drop any whose doc id disappeared. WHA-3614: do NOT auto-load a
        // model — the user must explicitly pick one in the ModelPicker.
        // Deep-links (?ifc=) still load their target via ModelPicker hydration.
        const availableIds = new Set(self.availableModels.map((m) => m.id));
        for (const m of self.loadedModels.slice()) {
          if (!availableIds.has(m.docId)) removeModel(m.docId);
        }
        self.loadStatus = "ready";
      } catch (e) {
        self.loadStatus = "error";
        self.error = e instanceof Error ? e.message : String(e);
      }
    });

    /** Set (or clear) the active project scope for the picker + empty state. */
    const setScopeProjectId = (projectId: number | null) => {
      self.scopeProjectId = projectId;
    };

    const reset = () => {
      cancelPendingCompare();
      self.availableModels.clear();
      self.loadedModels.clear();
      self.scopeProjectId = null;
      self.selectedElement = null;
      self.highlightContainer = null;
      self.highlightClassGroup = null;
      self.loadStatus = "idle";
      self.error = null;
      self.buildingId = null;
      self.orgId = null;
      // Compare state (WHA-3695).
      self.compareBaseDocId = null;
      self.compareTargetDocId = null;
      self.diff = null;
      self.compareStatus = "idle";
      self.compareError = null;
      self.compareVisibility = ChangeVisibility.create({});
      self.selectedChangeGuid = null;
      self.selectedGroupGuids.clear();
      self.activeCompareModel = "newer";
      self.filterStoreyGuids.clear();
      self.filterDisciplines.clear();
      self.filterClasses.clear();
      self.spacesVisible = false;
      self.changeDrawability = null;
    };

    return {
      loadModelsForBuilding,
      addModel,
      removeModel,
      setLoadedModels,
      setModelVisibility,
      selectElement,
      selectContainer,
      selectClassGroup,
      highlightSimilarClass,
      startCompare,
      swapCompare,
      exitCompare,
      setChangeDrawability,
      toggleChangeVisibility,
      selectChange,
      selectGroup,
      setActiveCompareModel,
      toggleFilterStorey,
      toggleFilterDiscipline,
      toggleFilterClass,
      clearCompareFilters,
      toggleSpacesVisible,
      setScopeProjectId,
      reset,
    };
  });

export type IfcModelStoreInstance = Instance<typeof IfcModelStore>;
